import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Tooltip, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useDispatch } from 'react-redux';
import { useContext } from 'react';
import { NotificationContext } from '../../../../providers/NotificationProvider';
import { cloneConsequence, getDeviation } from '../../../../features/projects/deviationSlice';
import { useTranslation } from 'react-i18next';

// Componente para renderizar filas de consecuencias
interface ConsequenceRowProps {
  data: {
    id: number | null;
    name: string;
    [key: string]: any;
  };
  order?: number;
  onEdit: (type: string, record: any) => void;
  onDelete: (type: string, record: any) => void;
  onClone?: (record: any) => void;
  editingItem: { type: string; id: number | null };
  editedFormValues: any;
  setEditedFormValues: any;
  modifyConsequence: any;
  setEditingItem: any;
  deviationId?: number;
}

const ConsequenceRow: React.FC<ConsequenceRowProps> = ({
  data,
  order,
  onEdit,
  onDelete,
  onClone,
  editingItem,
  editedFormValues,
  setEditedFormValues,
  modifyConsequence,
  setEditingItem,
  deviationId,
}) => {
  const { t } = useTranslation();
  const { Text } = Typography;
  const prefix = order !== undefined ? `${order}. ` : '';
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);

  const extractErrorMessage = (payload: unknown): string | null => {
    if (payload === null || payload === undefined) {
      return null;
    }
    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      return trimmed.length ? trimmed : null;
    }
    if (Array.isArray(payload)) {
      const messages = payload
        .map((item) => (typeof item === 'string' ? item.trim() : null))
        .filter((message): message is string => Boolean(message));
      return messages.length ? messages.join('\n') : null;
    }
    if (typeof payload === 'object') {
      const record = payload as Record<string, unknown>;
      if ('error' in record) {
        const nested = extractErrorMessage(record.error);
        if (nested) {
          return nested;
        }
      }
      const nestedMessages = Object.values(record)
        .map((value) => extractErrorMessage(value))
        .filter((message): message is string => Boolean(message));
      if (nestedMessages.length) {
        return nestedMessages.join('\n');
      }
      return null;
    }
    if (typeof payload === 'number' || typeof payload === 'boolean') {
      return String(payload);
    }
    return null;
  };

  if (editingItem.type === 'consequence' && editingItem.id === data.id) {
    return (
      <div style={{ marginBottom: '8px', padding: '8px', border: '1px solid #f0f0f0' }}>
        {order !== undefined && (
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            {`${order}.`}
          </Text>
        )}
        <TextArea
          value={editedFormValues.consequence.name}
          onChange={(e) =>
            setEditedFormValues((prev: any) => ({
              ...prev,
              consequence: { ...prev.consequence, name: e.target.value, id: data.id },
            }))
          }
          onPressEnter={modifyConsequence}
          onBlur={modifyConsequence}
          autoFocus
          size='small'
          style={{ width: 200, display: 'block' }}
        />
      </div>
    );
  }
  return (
    <div style={{ marginBottom: '8px', padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
      <Text style={{ width: 200, display: 'block' }}>{order !== undefined ? `${prefix}${data.name}` : data.name}</Text>
      <span style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
        <Tooltip title={t('Editar consecuencia')}>
          <Button icon={<EditOutlined />} onClick={() => onEdit('consequence', data)} size='small' type='text' />
        </Tooltip>
        <Tooltip title={t('Clonar consecuencia')}>
          <Button
            icon={<CopyOutlined />}
            onClick={() => {
            if (onClone) {
              onClone(data);
              return;
            }
            if (!data.id) return;
            dispatch(cloneConsequence(data.id) as any)
              .then((result: any) => {
                if (cloneConsequence.fulfilled.match(result)) {
                  triggerNotification(
                    'success',
                    t('Consecuencia clonada'),
                    t('La consecuencia ha sido clonada correctamente'),
                    'bottomRight'
                  );
                  if (deviationId) {
                    void dispatch(getDeviation(deviationId) as any);
                  }
                } else {
                  const message = extractErrorMessage(result.payload) ?? t('No se pudo clonar');
                  triggerNotification('error', t('Error'), message, 'bottomRight');
                }
              })
              .catch((error: any) => {
                const message = extractErrorMessage(error) ?? t('No se pudo clonar');
                triggerNotification('error', t('Error'), message, 'bottomRight');
              });
            }}
            size='small'
            type='text'
          />
        </Tooltip>
        <Tooltip title={t('Eliminar consecuencia')}>
          <Button
            icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
            onClick={() => onDelete('consequence', data)}
            size='small'
            type='text'
            danger
            aria-label={t('Eliminar consecuencia')}
          />
        </Tooltip>
      </span>
    </div>
  );
};

export default ConsequenceRow;
